/* eslint-disable no-prototype-builtins */
/* eslint-disable func-style */
import { User } from '@ms/core/model/user.model';
import { TranslateService } from '@ngx-translate/core';
import { ClassConstructor, plainToClass, plainToClassFromExist } from 'class-transformer';
import {
    interpolateRainbow,
    schemeAccent,
    schemeCategory10,
    schemeDark2,
    schemePaired,
    schemeSet1,
    schemeSet2,
    schemeSet3,
} from 'd3-scale-chromatic';
import { toNumber } from 'lodash-es';

import { UTCDate } from '@date-fns/utc';
import { EventStatus } from 'app/layout/components/content/calendar/calendar-event.model';
import { addMinutes, isDate, parseISO } from 'date-fns';
import { environment } from 'environments/environment';
import { NameOrder } from '../schools/model/school.model';
import { ColorPalette } from './model/color-palette.model';
import { MeetingStatusType } from './model/meetings-model.model';
import { Profile } from './model/profile.model';
import { SharedConstants } from './shared.constants';
export const _isoDateFormat = /^\d{4}-\d{2}-\d{2}/;
// "2020-07-15T12:22:21.942"
export const _isoDateTimeFormat = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

//Convert readonly object to mutable
export type Mutable<T> = {
    -readonly [P in keyof T]: T[P];
};

export const groupBy = (items, key) =>
    items.reduce(
        (result, item) => ({
            ...result,
            [item[key]]: [...(result[item[key]] || []), item],
        }),
        {}
    );

export function enumValues<T>(t: T) {
    return Object.keys(t).filter(type => isNaN(type as any) && type != 'values');
}

export function enumValuesExcept<T>(t: T, except: string | string[]) {
    if (typeof except == 'string') {
        return Object.keys(t).filter(type => isNaN(type as any) && type != 'values' && type != except);
    } else {
        return Object.keys(t).filter(type => isNaN(type as any) && type != 'values' && except.indexOf(type) == -1);
    }
}

export function enumType<T>(val, et: T) {
    let type;
    for (const key in et) {
        if (val == et[key]) {
            type = key;
            break;
        }
    }
    return type;
}

export function sortFullName(
    nameOrder: NameOrder,
    isAscending: boolean,
    a: { firstName: string; middleName: string; lastName: string },
    b: { firstName: string; middleName: string; lastName: string }
): number {
    console.log('Sorting');
    const d1 = getFullName(nameOrder, a.firstName, a.middleName, a.lastName);
    const d2 = getFullName(nameOrder, b.firstName, b.middleName, b.lastName);
    return compare(d1, d2, isAscending);
}

export function compareObjects(o1: any, o2: any): boolean {
    if (o1 && o1.id && o2 && o2.id) {
        return o1.id == o2.id;
    } else {
        return o1 == o2;
    }
}

export function compare(a: number | string | Date | boolean, b: number | string | Date | boolean, isAsc: boolean) {
    if (a != undefined && b == undefined) {
        return isAsc ? 1 : -1;
    } else if (b != undefined && a == undefined) {
        return isAsc ? -1 : 1;
    }
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}

export function toCurrencyFormat(value: number, mask: any): string {
    mask.typedValue = value; // use number
    return mask.value;
}

export function toCurrencyShortFormat(num, digits) {
    const si = [
        { value: 1, symbol: '' },
        { value: 1e3, symbol: 'K' },
        { value: 1e6, symbol: 'M' },
        { value: 1e9, symbol: 'G' },
        { value: 1e12, symbol: 'T' },
        { value: 1e15, symbol: 'P' },
        { value: 1e18, symbol: 'E' },
    ];
    const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
    let i;
    for (i = si.length - 1; i > 0; i--) {
        if (num >= si[i].value) {
            break;
        }
    }
    return (num / si[i].value).toFixed(digits).replace(rx, '$1') + si[i].symbol;
}

export const IBAN_MASK = 'aa00 0000 0000 0000 0000 00';

export const monthFields = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

export function unformat(principleService, str: string): number {
    if (str) {
        let value = str.split(principleService.profile.format.currency.thousandsSeparatorSymbol).join('');
        value = value.split(principleService.profile.format.currency.decimalSymbol).join('.');
        value = value.replace('-.', '-0.').replace('-_', '').replace('_', '');
        return toNumber(value);
    } else {
        return 0;
    }
}

//https://github.com/typestack/class-transformer
export function cloneObjectFromExist<T, V>(clsObject: T, plain: V) {
    return plainToClassFromExist(clsObject, plain as Object, {
        excludePrefixes: ['dateWithTimeFields'],
    });
}

//https://github.com/typestack/class-transformer
export function cloneObject<T>(targetClass: ClassConstructor<T>, source: any) {
    return plainToClass(targetClass, source as Object, {
        excludePrefixes: ['dateWithTimeFields'],
    });
}

export function deepCopy(data: any) {
    //return structuredClone(data);

    let node;
    if (Array.isArray(data)) {
        node = data.length > 0 ? data.slice(0) : [];
        node.forEach((e, i) => {
            if (typeof e == 'object' || (Array.isArray(e) && e.length > 0)) {
                node[i] = deepCopy(e);
            }
        });
    } else if (data && typeof data == 'object') {
        node = data instanceof Date ? data : Object.assign({}, data);
        Object.keys(node).forEach(key => {
            if (typeof node[key] == 'object' || (Array.isArray(node[key]) && node[key].length > 0)) {
                node[key] = deepCopy(node[key]);
            }
        });
    } else {
        node = data;
    }
    return node;
}

export function deepMerge(setUndefined: boolean, target, ...sources) {
    if (!sources.length) {
        return target;
    }
    const source = sources.shift();

    if (isObject(target) && isObject(source)) {
        for (const key in source) {
            if (isObject(source[key]) && !(source[key] instanceof Date)) {
                if (!target[key]) {
                    Object.assign(target, { [key]: {} });
                }
                deepMerge(setUndefined, target[key], source[key]);
            } else {
                if (setUndefined || source[key] != undefined) Object.assign(target, { [key]: source[key] });
            }
        }
    }

    return deepMerge(setUndefined, target, ...sources);
}

export function isObject(item) {
    return item && typeof item == 'object' && !Array.isArray(item);
}

export function getFullNameProfile(nameOrder: NameOrder, profile: Profile, shortVersion = false) {
    return getFullName(nameOrder, profile.firstName, profile.middleName, profile.lastName, shortVersion);
}

export function getFullName(
    nameOrder: NameOrder,
    firstName: string,
    middleName: string,
    lastName: string,
    shortVersion = false
) {
    if (!firstName) {
        return '';
    }
    const fn = shortVersion && firstName?.length > 0 ? `${firstName.substring(0, 1)}.` : firstName;
    const mn =
        shortVersion && middleName?.length > 0
            ? `${middleName.substring(0, 1)}.`
            : middleName?.length > 0
              ? middleName
              : '';
    if (nameOrder == NameOrder.FIRST_LAST) {
        return mn?.length > 0 ? `${fn} ${mn} ${lastName}` : `${fn} ${lastName}`;
    }
    if (nameOrder == NameOrder.LAST_FIRST) {
        return mn?.length > 0 ? `${lastName}, ${fn} ${middleName.substring(0, 1)}` : `${lastName}, ${fn}`;
    }
    return mn?.length > 0 ? `${fn} ${mn} ${lastName}` : `${fn} ${lastName}`;
}

export function calculatePoint(i: number, intervalSize: number, colorRangeInfo): number {
    const { colorStart, colorEnd, useEndAsStart } = colorRangeInfo;
    return useEndAsStart ? colorEnd - i * intervalSize : colorStart + i * intervalSize;
}

export function interpolateColors(dataLength: number): string[] {
    const colorRangeInfo = {
        colorStart: 0,
        colorEnd: 1,
        useEndAsStart: true,
    };
    const colorScale = interpolateRainbow;

    const { colorStart, colorEnd } = colorRangeInfo;
    const colorRange = colorEnd - colorStart;
    const intervalSize = colorRange / dataLength;
    let colorArray = [];

    if (dataLength > 30) {
        for (let i = 0; i < dataLength; i++) {
            const colorPoint = calculatePoint(i, intervalSize, colorRangeInfo);
            colorArray.push(colorScale(colorPoint));
        }
    } else {
        colorArray = schemeCategory10
            .concat(schemeAccent)
            .concat(schemeDark2)
            .concat(schemePaired)
            .concat(schemeSet2)
            .concat(schemeSet1)
            .concat(schemeSet3)
            .concat(schemePaired);
    }
    // console.log(colorArray);

    return colorArray;
}

export function comparer(otherArray: any[]) {
    return function (current: any) {
        return (
            otherArray.filter(other => {
                if (other.id) {
                    return other.id == current.id;
                } else if (other.name) {
                    return other.name == current.name;
                } else {
                    return false;
                }
            }).length == 0
        );
    };
}

export function isAdminOrHeadTeacher(user: User): boolean {
    return user.roles.some(
        role =>
            role == SharedConstants.ROLE_HEAD_TEACHER ||
            role == SharedConstants.ROLE_TENANT_ADMIN ||
            role == SharedConstants.ROLE_ADMIN
    );
}

export function isAdmin(user: User): boolean {
    return user.roles.some(role => role == SharedConstants.ROLE_TENANT_ADMIN || role == SharedConstants.ROLE_ADMIN);
}

export function isHeadTeacher(user: User): boolean {
    return user.roles.some(role => role == SharedConstants.ROLE_HEAD_TEACHER);
}

export function openWindow(url: string, name: string, width: number, height: number) {
    const leftPosition = window.screen.width / 2 - (width / 2 + 10);
    const topPosition = window.screen.height / 2 - (height / 2 + 50);
    return window.open(
        url,
        name,
        `popup,status=no,height=${height},width=${width},resizable=yes,left=${leftPosition},top=${topPosition},screenX=${leftPosition},screenY=${topPosition},toolbar=no,menubar=no,scrollbars=no,location=no,directories=no`
    );
}

export function getEnumKeyByEnumValue<T extends { [index: string]: string }>(
    myEnum: T,
    enumValue: string
): keyof T | null {
    const keys = Object.keys(myEnum).filter(x => myEnum[x] == enumValue);
    return keys.length > 0 ? keys[0] : null;
}

export function getDurationTime(duration: number, translateService: TranslateService): string {
    if (!duration || duration < 1) {
        return '—';
    }
    const d = Math.floor(duration / (3600 * 24));
    const h = Math.floor((duration % (3600 * 24)) / 3600);
    const m = Math.floor((duration % 3600) / 60);
    const s = Math.floor(duration % 60);

    const dDisplay = d > 0 ? `${d} ${translateService.instant('GENERAL.TITLE.D')} ` : '';
    const hDisplay = h > 0 ? `${h} ${translateService.instant('GENERAL.TITLE.H')} ` : '';
    const mDisplay = m > 0 ? `${m} ${translateService.instant('GENERAL.TITLE.M')} ` : '';
    const sDisplay = s > 0 ? `${s} ${translateService.instant('GENERAL.TITLE.S')}` : '';
    return dDisplay + hDisplay + mDisplay + sDisplay;
}
export const colors: ColorPalette[] = [
    { name: 'Red', value: '#ff1744', foreground: 'white' },
    { name: 'Pink', value: '#ff80ab', foreground: 'black' },
    { name: 'Purple-1', value: '#d500f9', foreground: 'white' },
    //{ name: 'Purple-2', value: '#361E54', foreground: '#FD9C3D' },
    { name: 'Purple-2', value: '#361E54', foreground: '#FEBA77' },
    { name: 'Deep Purple', value: '#361e54', foreground: 'white' },
    { name: 'Indigo', value: '#3d5afe', foreground: 'white' },
    { name: 'Blue', value: '#2979ff', foreground: 'white' },
    { name: 'Light Blue', value: '#00b0ff', foreground: 'black' },
    { name: 'Cyan', value: '#00e5ff', foreground: 'black' },
    { name: 'Teal', value: '#1de9b6', foreground: 'black' },
    { name: 'Green', value: '#00e676', foreground: 'black' },
    { name: 'Light Green', value: '#76ff03', foreground: 'black' },
    { name: 'Lime', value: '#c6ff00', foreground: 'black' },
    { name: 'Yellow', value: '#ffea00', foreground: 'black' },
    { name: 'Amber', value: '#ffc400', foreground: 'black' },
    { name: 'Orange', value: '#ff9100', foreground: 'black' },
    { name: 'Deep Orange', value: '#ff3d00', foreground: 'white' },
    { name: 'Brown', value: '#8d6e63', foreground: 'white' },
    { name: 'Light Gray', value: '#bdbdbd', foreground: 'black' },
    { name: 'Dark Gray', value: '#616161', foreground: 'white' },
    { name: 'Blue Gray', value: '#78909c', foreground: 'white' },
    { name: 'Flame', value: '#e45a33', foreground: 'white' },
    { name: 'Orange', value: '#fa761e', foreground: 'white' },
    { name: 'Infrared', value: '#ef486e', foreground: 'white' },
    { name: 'Male Color', value: '#4488ff', foreground: 'white' },
    { name: 'Female Color', value: '#ff44aa', foreground: 'white' },
    { name: 'Pale Yellow', value: '#ffd165', foreground: 'black' },
    { name: 'Gargoyle Gas', value: '#fde84e', foreground: 'black' },
    { name: 'Android Green', value: '#9ac53e', foreground: 'black' },
    { name: 'Carribean Green', value: '#05d59e', foreground: 'white' },
    { name: 'Blue Jeans', value: '#5bbfea', foreground: 'white' },
    { name: 'Cyan Cornflower', value: '#1089b1', foreground: 'white' },
    { name: 'Green-1', value: '#17460a', foreground: 'white' },
    { name: 'Green-2', value: '#2b6315', foreground: 'white' },
    { name: 'Green-3', value: '#326e1a', foreground: 'white' },
    { name: 'Green-4', value: '#2dfc00', foreground: 'black' },
    { name: 'Blue-1', value: '#1b278d', foreground: 'white' },
    { name: 'Blue-2', value: '#29379d', foreground: 'white' },
    { name: 'Blue-3', value: '#4757ff', foreground: 'black' },
    { name: 'Blue-4 ', value: '#1d00e1', foreground: 'white' },
    { name: 'Blue-5', value: '#2000fa', foreground: 'white' },
];

export function fileIcon(filename: string): string {
    if (!filename) {
        return 'file';
    }
    const pos = filename.lastIndexOf('.');
    if (pos == -1) return 'file';
    const ext = filename.substring(pos + 1).toLowerCase();
    if (ext == 'pdf') {
        return 'file-pdf';
    } else if (ext == 'doc' || ext == 'docx') {
        return 'file-word';
    } else if (ext == 'avi' || ext == 'wmv' || ext == 'mkv' || ext == 'webm' || ext == 'mp4' || ext == 'mov') {
        return 'file-video';
    } else if (ext == 'm4a' || ext == 'flac' || ext == 'mp3' || ext == 'wav' || ext == 'wma' || ext == 'aac') {
        return 'file-audio';
    } else if (
        ext == 'png' ||
        ext == 'jpg' ||
        ext == 'jpeg' ||
        ext == 'tif' ||
        ext == 'tiff' ||
        ext == 'gif' ||
        ext == 'raw'
    ) {
        return 'file-image';
    } else if (ext == 'zip' || ext == 'rar' || ext == 'zipx' || ext == '7z' || ext == 's7z' || ext == 'ace') {
        return 'file-archive';
    } else if (ext == 'xls' || ext == 'xlsx' || ext == 'xlsm') {
        return 'file-excel';
    } else if (ext == 'ppt' || ext == 'pptx') {
        return 'file-powerpoint';
    } else {
        return 'file';
    }
}
export function isDateTimeDecoratorUsingInstance(instance: any, propertyKey: string) {
    console.log('FIELDS', instance.dateWithTimeFields);
    return instance.dateWithTimeFields?.length > 0 ? instance.dateWithTimeFields.indexOf(propertyKey) > -1 : false;
}

export function convertRequest(body: any) {
    //console.log('DECORATORS', body.dateWithTimeFields);
    if (body == null || body == undefined) {
        return body;
    }
    if (typeof body != 'object') {
        return body;
    }
    for (const key of Object.keys(body)) {
        const value = body[key];
        if (value) {
            if (isDate(value)) {
                //console.log("DATE-TIME", Reflect.getMetadata("dateTime", body), key);
                if (!isDateTimeDecoratorUsingInstance(body, key)) {
                    body[key] = new UTCDate(value.getFullYear(), value.getMonth(), value.getDate());
                } else {
                    console.log('TIMES-ALIGNED', key);
                    console.log('DATE_WITH_TIME - DECORATOR FOUND');
                    body[key] = new UTCDate(
                        value.getFullYear(),
                        value.getMonth(),
                        value.getDate(),
                        value.getHours(),
                        value.getMinutes()
                    );
                }
            } else if (body.hasOwnProperty(key) && typeof body[key] == 'object') {
                body[key] = convertRequest(body[key]);
            }
        }
    }
    return body;
}

export function isIsoDateString(value: any): boolean {
    if (value == null || value == undefined) {
        return false;
    }
    if (value.length == 10 && typeof value == 'string') {
        return _isoDateFormat.test(value);
    }
    return false;
}

export function isIsoDateTimeString(value: any): boolean {
    if (value == null || value == undefined) {
        return false;
    }
    if ((value.length >= 16 || value.length <= 23) && typeof value == 'string') {
        return _isoDateTimeFormat.test(value);
    }
    return false;
}

export function convertResponse(body: any) {
    //const sourceOffset = new Date().getTimezoneOffset();
    // console.log('Convert', body);
    if (body == null || body == undefined) {
        return body;
    }
    if (typeof body != 'object') {
        return body;
    }
    for (const key of Object.keys(body)) {
        let value = body[key];
        if (typeof value == 'string') {
            if (isIsoDateString(value)) {
                body[key] = parseISO(value);
            } else if (isIsoDateTimeString(value)) {
                //console.log('RESPONSE-DATA-RAW-DATE', value, environment.timezoneOffset);
                value = addMinutes(parseISO(value), environment.timezoneOffset); // USER LOCAL TIME
                //console.log('RESPONSE-DATA-CONVERTED-UTC', value.toString());
                //value = subMinutes(value, zoneOffset);
                //body[key] = toZonedTime(value, zoneOffset);
                body[key] = value;
                //console.log('RESPONSE-DATA-CONVERTED-SCHOOL', body[key].toString());
            }
        } else if (value instanceof Date) {
        } else if (typeof value == 'object') {
            body[key] = convertResponse(value);
        }
    }
    return body;
}

export function toBoolean(val: string) {
    if (!val) {
        return false;
    }
    switch (val.toLowerCase()) {
        case 'true':
        case '1':
        case 'yes':
        case 'on':
        case 'y':
            return true;
        case 'false':
        case '0':
        case 'no':
        case 'off':
        case 'n':
            return false;
        default:
            return false;
    }
}

export function equalPrimitives<T>(a: T, b: T): boolean {
    return (a == null || typeof a != 'object') && Object.is(a, b);
}

export function getEventStatusColor(status: EventStatus) {
    if (status == EventStatus.STARTED) {
        return 'info-text';
    } else if (status == EventStatus.ENDED) {
        return 'success-text';
    } else if (status == EventStatus.PUBLISHED) {
        return 'warning-text';
    } else if (status == EventStatus.CANCELLED) {
        return 'danger-text';
    } else {
        return 'warning-text';
    }
}

export function getMeetingStatusColor(status: MeetingStatusType) {
    if (status == MeetingStatusType.STARTED) {
        return 'info-text';
    } else if (status == MeetingStatusType.ENDED) {
        return 'success-text';
    } else if (status == MeetingStatusType.PUBLISHED) {
        return 'warning-text';
    } else if (status == MeetingStatusType.CANCELLED) {
        return 'danger-text';
    } else {
        return 'warning-text';
    }
}

export const replaceOrAppend = (arr: any[], val: any, compFn) => {
    const res = [...arr];
    const i = arr.findIndex(v => compFn(v, val));
    if (i === -1) res.push(val);
    else res.splice(i, 1, val);
    return res;
};

// const items = arrayOfItems.filters(isPresent)
export function isPresent<T>(t: T | undefined | null): t is T {
    return t !== undefined && t !== null;
}

//Ex: filterNaN = (n: number): number => filterNaN(n);
export const filterNaN = (input: number): number => (isNaN(input) ? 0 : input);
